import {
  ArrowDownIcon,
  ArrowUpIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  type HannibalConfigInitial,
  LaunchHannibalDialog,
} from '@/sections/insights/aiAssistant/components/LaunchHannibalDialog';
import { AGGRESSIVENESS_OPTIONS } from '@/sections/insights/aiAssistant/types/hunt.types';
import {
  HUNT_AGENT_OPTIONS,
  type HuntScheduleConfig,
  type HuntScheduleEntry,
  newEntry,
} from '@/sections/settings/components/forms/huntScheduleConstants';
import { cn } from '@/utils/classname';

interface HuntScheduleStepProps {
  config: HuntScheduleConfig;
  onChange: (config: HuntScheduleConfig) => void;
  disabled?: boolean;
}

function formatRelativeTime(iso: string): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  if (diffMs < 0) return 'Never';
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function HuntScheduleStep({
  config,
  onChange,
  disabled,
}: HuntScheduleStepProps) {
  // { open } drives the wizard; id is the entry being edited (null = adding).
  const [editing, setEditing] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });

  const closeDialog = () => setEditing({ open: false, id: null });

  const saveProfile = (cfg: {
    mandate: string;
    agent: string;
    scope: string[];
    aggressiveness: HuntScheduleEntry['aggressiveness'];
    name?: string;
  }) => {
    const fields = {
      name: (cfg.name ?? '').trim() || `Hunt ${config.entries.length + 1}`,
      agent: cfg.agent,
      scope: cfg.scope,
      prompt: cfg.mandate,
      aggressiveness: cfg.aggressiveness,
    };
    onChange({
      ...config,
      entries: editing.id
        ? config.entries.map(e =>
            e.id === editing.id ? { ...e, ...fields } : e
          )
        : [...config.entries, { ...newEntry(), ...fields }],
    });
    closeDialog();
  };

  const removeEntry = (id: string) =>
    onChange({ ...config, entries: config.entries.filter(e => e.id !== id) });

  const moveEntry = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= config.entries.length) return;
    const next = [...config.entries];
    [next[index], next[target]] = [next[target], next[index]];
    onChange({ ...config, entries: next });
  };

  const editingEntry = editing.id
    ? config.entries.find(e => e.id === editing.id)
    : undefined;
  const initial: HannibalConfigInitial | undefined = editingEntry
    ? {
        name: editingEntry.name,
        agent: editingEntry.agent,
        scope: editingEntry.scope,
        mandate: editingEntry.prompt,
        aggressiveness: editingEntry.aggressiveness,
      }
    : undefined;

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="border-chariot-stroke bg-chariot-background-secondary flex items-center justify-between gap-4 rounded-lg border px-4 py-3">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="hunt-schedule-enabled"
            className="text-chariot-text-primary text-sm font-medium"
          >
            Automatic Scoped Hunts
          </label>
          <span className="text-chariot-text-secondary text-xs">
            Configure hunt profiles that rotate automatically when budget
            headroom is available. Without any profiles, automatic hunts run
            account-wide with the default mandate.
          </span>
        </div>
        <Switch
          id="hunt-schedule-enabled"
          checked={config.enabled}
          onCheckedChange={checked => onChange({ ...config, enabled: checked })}
          disabled={disabled}
          data-testid="hunt-schedule-enabled"
        />
      </div>

      {config.enabled && (
        <>
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
            {config.entries.length === 0 && (
              <p className="text-chariot-text-secondary py-8 text-center text-sm">
                No scoped hunt profiles configured. Automatic hunts will run
                account-wide. Add a profile to target specific assets.
              </p>
            )}

            {config.entries.map((entry, index) => {
              const agentLabel =
                HUNT_AGENT_OPTIONS.find(o => o.value === entry.agent)?.label ??
                entry.agent;
              const aggressivenessLabel =
                AGGRESSIVENESS_OPTIONS.find(
                  o => o.value === entry.aggressiveness
                )?.label ?? 'Balanced';
              const scopeLabel =
                entry.scope.length > 0
                  ? `${entry.scope.length} target${entry.scope.length !== 1 ? 's' : ''}`
                  : 'Account-wide';

              return (
                <div
                  key={entry.id}
                  className="border-chariot-stroke flex items-center gap-3 rounded-lg border px-4 py-3"
                  data-testid={`hunt-entry-${entry.id}`}
                >
                  <span className="text-chariot-text-secondary text-xs font-medium tabular-nums">
                    {index + 1}
                  </span>
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 flex-col gap-0.5 text-left"
                    onClick={() => setEditing({ open: true, id: entry.id })}
                    disabled={disabled}
                    data-testid={`hunt-entry-edit-${entry.id}`}
                  >
                    <span className="text-chariot-text-primary truncate text-sm font-medium">
                      {entry.name || 'Untitled'}
                    </span>
                    <span className="text-chariot-text-secondary truncate text-xs">
                      {agentLabel} · {scopeLabel} · {aggressivenessLabel}
                    </span>
                  </button>
                  <span className="text-chariot-text-secondary shrink-0 text-xs">
                    Last ran: {formatRelativeTime(entry.last_run_at)}
                  </span>
                  <div className="flex shrink-0 gap-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => moveEntry(index, -1)}
                      disabled={disabled || index === 0}
                      aria-label="Move up"
                    >
                      <ArrowUpIcon className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => moveEntry(index, 1)}
                      disabled={disabled || index === config.entries.length - 1}
                      aria-label="Move down"
                    >
                      <ArrowDownIcon className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing({ open: true, id: entry.id })}
                      disabled={disabled}
                      aria-label="Edit profile"
                    >
                      <PencilSquareIcon className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'text-chariot-stroke-critical hover:text-chariot-stroke-critical'
                      )}
                      onClick={() => removeEntry(entry.id)}
                      disabled={disabled}
                      aria-label="Remove profile"
                      data-testid="hunt-entry-remove"
                    >
                      <TrashIcon className="size-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 self-start"
            onClick={() => setEditing({ open: true, id: null })}
            disabled={disabled}
            data-testid="hunt-schedule-add-entry"
          >
            <PlusIcon className="mr-1 size-4" />
            Add hunt profile
          </Button>
        </>
      )}

      <LaunchHannibalDialog
        open={editing.open}
        mission=""
        isPending={false}
        variant="profile"
        initial={initial}
        onLaunch={saveProfile}
        onCancel={closeDialog}
      />
    </div>
  );
}
